import { useState, useCallback, useEffect, useRef } from 'react'
import { api } from '../utils/api'
import { track, errorCategory, sizeMb } from '../utils/analytics'
import type { ConversionSettings, ConversionResult, ConversionStage } from '../types/conversion'

const INTERVAL = 100

const WEIGHTS = {
  uploading: 0.1,  // 10%
  video: 0.1,      // 10%
  pdf: 0.2,        // 20%
  images: 0.2,     // 20%
  scorm: 0.3,      // 30%
  packaging: 0.1   // 10%
} as const

const initial = {
  uploading: 0,
  video: 0,
  pdf: 0,
  images: 0,
  scorm: 0,
  packaging: 0
}

function calculateTotalProgress(progress: typeof initial, currentStage: ConversionStage): number {
  const stages = Object.keys(WEIGHTS) as ConversionStage[];
  const currentStageIndex = stages.indexOf(currentStage);
  let total = 0;
  
  stages.forEach((stage, index) => {
    if (index < currentStageIndex) {
      // Все предыдущие этапы считаем завершенными
      total += WEIGHTS[stage] * 100;
    } else if (stage === currentStage) {
      // Текущий этап - его актуальный прогресс
      if (progress[stage] === 0 && index > 0) {
        // Если текущий этап только начался и это не первый этап,
        // добавляем предыдущий этап как завершенный
        total += WEIGHTS[stages[index - 1]] * 100;
      }
      total += WEIGHTS[stage] * progress[stage];
    }
    // Будущие этапы остаются на 0
  });
  return total;
}

export function useConversion() {
  const [isConverting, setIsConverting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentStage, setCurrentStage] = useState<ConversionStage>('uploading')
  const [timeLeft, setTimeLeft] = useState<number | null>(null)
  const [conversionResult, setConversionResult] = useState<ConversionResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [downloadAttempted, setDownloadAttempted] = useState(false)

  const lastUpdate = useRef(0)
  const stageProgress = useRef({ ...initial })
  const maxProgressSeen = useRef(0) // Track maximum progress seen

  const updateProgress = useCallback((stage: ConversionStage, value: number) => {
    const now = Date.now()
    const normalized = value <= 1 ? value * 100 : value
    
    stageProgress.current[stage] = normalized

    if (now - lastUpdate.current >= INTERVAL) {
      const total = calculateTotalProgress(stageProgress.current, stage)
      
      // Ensure progress never decreases
      const monotonicalProgress = Math.max(total, maxProgressSeen.current)
      maxProgressSeen.current = monotonicalProgress
      
      requestAnimationFrame(() => {
        setCurrentStage(stage)
        setProgress(Math.round(monotonicalProgress))
      })

      lastUpdate.current = now
    }
  }, [])

  const startConversion = useCallback(async (file: File, settings: ConversionSettings) => {
    // Analytics lives in the hook, not in the pages: both the funnel and any future
    // caller of startConversion are then measured without a second call site.
    const fileSizeMb = sizeMb(file.size)
    const startedAt = Date.now()
    try {
      setIsConverting(true)
      setError(null)
      setProgress(0)
      setCurrentStage('uploading')
      stageProgress.current = { ...initial }
      maxProgressSeen.current = 0 // Reset max progress when starting new conversion

      track('conversion_started', { file_size_mb: fileSizeMb })
      const result = await api.convertPresentation(file, settings, updateProgress)
      setProgress(100)
      setConversionResult(result)
      track('conversion_completed', {
        file_size_mb: fileSizeMb,
        duration_seconds: Math.round((Date.now() - startedAt) / 1000)
      })
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(`Error: ${message}`)
      track('conversion_failed', { file_size_mb: fileSizeMb, error_category: errorCategory(message) })
      throw err
    } finally {
      setIsConverting(false)
    }
  }, [updateProgress])

  useEffect(() => {
    if (!isConverting) {
      setTimeLeft(null)
      return
    }
    setTimeLeft(Math.ceil(((100 - progress) / 25) * 15))
  }, [progress, isConverting])

  const onDownloadAttempted = useCallback(() => {
    setDownloadAttempted(true)
  }, [])

  const handleDownload = useCallback(() => {
    if (conversionResult?.downloadUrl) {
      // GA4's automatic file_download never fires here: this is a JS navigation to an
      // extensionless API URL, not a click on a link ending in .zip.
      track('scorm_downloaded')
      window.location.href = conversionResult.downloadUrl
      onDownloadAttempted()
    }
  }, [conversionResult, onDownloadAttempted])

  const handleNewConversion = useCallback(() => {
    setConversionResult(null)
    setProgress(0)
    setError(null)
    setCurrentStage('uploading')
    setTimeLeft(null)
    setDownloadAttempted(false)
    stageProgress.current = { ...initial }
    maxProgressSeen.current = 0 // Reset max progress when starting a new conversion
  }, [])

  const setErrorMessage = useCallback((message: string | null) => {
    setError(message ? `Error: ${message}` : null)
  }, [])

  return {
    isConverting,
    progress,
    currentStage,
    timeLeft,
    conversionResult,
    error,
    downloadAttempted,
    startConversion,
    onDownloadAttempted,
    handleDownload,
    handleNewConversion,
    setErrorMessage
  }
}
